import clsx from 'clsx'
import { forwardRef, useEffect, useState } from 'react'

import { TextField, Props as TextFieldProps } from './TextField'

type DollarString = `$${string}`

type Props = Omit<TextFieldProps, 'mask' | 'value' | 'onChange' | 'onBlur'> & {
    value?: number | DollarString
    onChange: (newValue: number | undefined) => void
    formatCurrency?: boolean
    allowEnterToPropagate?: boolean
}

export const CurrencyField = forwardRef<HTMLInputElement, Props>(
    (
        {
            value,
            onChange,
            onFocus,
            size,
            icon,
            formatCurrency,
            allowEnterToPropagate = false,
            className,
            onClear,
            ...props
        },
        ref
    ) => {
        const [stringValue, setStringValue] = useState(
            typeof value === 'number' ? value.toFixed(2) : (value ?? '')
        )

        useEffect(() => {
            setStringValue(typeof value === 'number' ? value.toFixed(2) : (value ?? ''))
        }, [value])

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') {
                return
            }
            event.preventDefault()
            if (!allowEnterToPropagate) {
                event.stopPropagation()
            }
            event.currentTarget.blur()
        }

        const mask = formatCurrency
            ? {
                  mask: '{$}DDFF',
                  blocks: {
                      DD: {
                          mask: /^\d+$/,
                      },
                      FF: {
                          mask: /^[.]\d{0,2}$/,
                      },
                  },
                  lazy: false, // prevent $ from being deleted
              }
            : { mask: Number, scale: 2, radix: '.', min: 0 }

        return (
            <TextField
                {...props}
                ref={ref}
                value={stringValue}
                onChange={() => {
                    // Intentionally left blank. This is handled by the onComplete event in the mask library.
                }}
                mask={[
                    mask,
                    {
                        onComplete: (e) => {
                            setStringValue(e)
                        },
                    },
                ]}
                onClear={
                    onClear &&
                    (() => {
                        setStringValue('')
                        onClear()
                    })
                }
                onBlur={() => {
                    if ((!formatCurrency && Number.isNaN(Number(stringValue))) || !stringValue) {
                        onChange(undefined)
                        return
                    }

                    setStringValue(typeof value === 'number' ? value.toFixed(2) : (value ?? ''))

                    if (formatCurrency && stringValue.startsWith('$')) {
                        onChange(Number(stringValue.slice(1).replaceAll(',', '')))
                    }
                    else {
                        const parsedValue = parseFloat(stringValue)
                        if (Number.isNaN(parsedValue)) {
                            onChange(undefined)
                        }
                        else {
                            onChange(parsedValue)
                        }
                    }
                }}
                onFocus={(event) => {
                    event.target.select()
                    onFocus?.(event)
                }}
                onKeyDown={handleKeyDown}
                size={size}
                icon={
                    icon === null
                        ? null
                        : (icon ?? (size === 'small' ? 'dollar9.small' : 'dollar9.large'))
                }
                step={1}
                placeholder={formatCurrency ? '$0.00' : '0.00'}
                className={clsx(className)}
            />
        )
    }
)
CurrencyField.displayName = 'CurrencyField'
